import React, { useEffect, useRef, useState } from "react";
import { getGallery } from "../../api";
import { useInfiniteQuery } from "@tanstack/react-query";
import notFound from "../../images/not-found.png";
import { api_url } from "../../constant";
import { useTranslation } from "react-i18next";

export default function Gallery() {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [allItems, setAllItems] = useState([]); // Store all loaded items

  const {
    data,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["gallery"],
    queryFn: ({ pageParam = 1 }) => getGallery({ page: pageParam, limit: 12 }),
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.pageCount ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
  });

  // Combine all pages into one array
  React.useEffect(() => {
    if (data?.pages) {
      const allItems = data.pages.flatMap(page => page.data);
      setAllItems(allItems);
    }
  }, [data]);

  const openModal = (index) => setSelectedIndex(index);
  const closeModal = () => setSelectedIndex(null);

  const nextImage = () => {
    setSelectedIndex((prev) =>
      prev === allItems.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? allItems.length - 1 : prev - 1
    );
  };

  const loadMore = () => {
    fetchNextPage();
  };

  if (error) {
    return (
      <div className="flex flex-col min-h-[80vh] items-center justify-center">
        <div className="text-red-500 text-lg">
          {/* حدث خطأ في تحميل المعرض */}
          {t("gallery.load_error")}
        </div>
      </div>
    );
  }

  if (!isFetching && allItems.length === 0) {
    return (
      <div className="flex flex-col min-h-[80vh] items-center justify-center">
        <div className="font-bold mx-auto">
          <img src={notFound} alt="No gallery items found" />
        </div>
        <div className="h-6"></div>
        <div className="flex items-center mx-auto justify-center h-12 text-lg font-bold">
          {/* لا توجد صور في المعرض في الوقت الحالي */}
          {t("gallery.no_items")}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 lg:w-[90%] lg:mx-auto my-14">
      <h1 className="text-3xl font-medium mb-6 text-center">
        {t("gallery.title")}
      </h1>

      {/* Gallery Grid */}
      {isFetching && allItems.length === 0 ? (
        <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          {[...Array(12).keys()].map((e, i) => (
            <div
              key={i}
              className="break-inside-avoid bg-slate-50 rounded-lg overflow-hidden border-2 animate-pulse mb-4"
              style={{
                height: `${200 + Math.random() * 300}px`,
                animationDelay: `${100 * i}ms`,
              }}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4">
            {allItems.map((item, index) => (
              <MasonryGalleryCard
                key={`${item.id}-${index}`}
                item={item}
                onClick={() => openModal(index)}
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasNextPage && (
            <div className="text-center mt-10">
              <button
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className=" bg-primary text-primary disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded-md shadow-md transition-all duration-200"
              >
                {isFetchingNextPage ? t("gallery.loading") : t("gallery.load_more")}
              </button>
            </div>
          )}

          {/* Loading more skeleton */}
          {isFetchingNextPage && (
            <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4 mt-4">
              {[...Array(6).keys()].map((e, i) => (
                <div
                  key={`loading-${i}`}
                  className="break-inside-avoid bg-slate-50 rounded-lg overflow-hidden border-2 animate-pulse mb-4"
                  style={{
                    height: `${200 + Math.random() * 300}px`,
                    animationDelay: `${100 * i}ms`,
                  }}
                />
              ))}
            </div>
          )}

          {/* Modal Viewer */}
          {selectedIndex !== null && (
            <ModalViewer
              item={allItems[selectedIndex]}
              onClose={closeModal}
              onNext={nextImage}
              onPrev={prevImage}
              currentIndex={selectedIndex}
              totalItems={allItems.length}
            />
          )}
        </>
      )}
    </div>
  );
}

// 🖼️ Masonry Gallery Card
const MasonryGalleryCard = ({ item, onClick }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return notFound;
    return imagePath.startsWith("http")
      ? imagePath
      : `${api_url}/${imagePath.replace(/^\//, "")}`;
  };

  const randomHeight = 200 + Math.random() * 200;

  return (
    <div
      className="break-inside-avoid mb-4 group relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
      style={{ height: `${randomHeight}px` }}
      onClick={onClick}
    >
      <img
        src={getImageUrl(item.image)}
        alt={item.title}
        className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-110"
        loading="lazy"
      />

      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300" />

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white">
        <h3 className="text-lg font-semibold mb-1 line-clamp-1">
          {item.title}
        </h3>
        {item.description && (
          <p className="text-sm opacity-90 line-clamp-2">
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
};

export const ModalViewer = ({ item, onClose, onNext, onPrev, currentIndex, totalItems }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return notFound;
    return imagePath.startsWith("http")
      ? imagePath
      : `${api_url}/${imagePath.replace(/^\//, "")}`;
  };

  const overlayRef = useRef(null);

  // ✅ Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNext, onPrev]);

  // ✅ Close when clicking outside image
  const handleClickOutside = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleClickOutside}
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-zoom-out"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white text-3xl font-bold hover:text-gray-300 z-10"
      >
        &times;
      </button>

      {/* Prev */}
      <button
        onClick={onPrev}
        className="absolute left-6 text-white text-3xl font-bold hover:text-gray-300 z-10"
      >
        ❮
      </button>

      {/* Image */}
      <img
        src={getImageUrl(item.image)}
        alt={item.title}
        className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl object-contain cursor-default"
      />

      {/* Next */}
      <button
        onClick={onNext}
        className="absolute right-6 text-white text-3xl font-bold hover:text-gray-300 z-10"
      >
        ❯
      </button>

      {/* Title & Counter */}
      <div className="absolute bottom-10 left-0 right-0 text-center">
        {item.title && (
          <div className="text-white text-lg bg-black/60 px-4 py-2 rounded-lg inline-block mx-auto mb-2">
            {item.title}
          </div>
        )}
        <div className="text-white text-sm bg-black/40 px-3 py-1 rounded-full inline-block">
          {currentIndex + 1} / {totalItems}
        </div>
      </div>
    </div>
  );
};